import { GridColumnVisibilityModel, GridSortModel } from '@mui/x-data-grid-pro';

import { useColumnOrder } from './useColumnOrder';
import { useColumnVisibility } from './useColumnVisibility';
import { useColumnWidths } from './useColumnWidths';
import { useDensity } from './useDensity';
import { useSortModel } from './useSortModel';

/**
 * Storage key shape for table preference persistence.
 * Use `satisfies TablePreferenceStorageKeys` on your page's storage key constant to
 * enforce this contract at compile time.
 */
export type TablePreferenceStorageKeys = {
  viewMode: string;
  tableColumnVisibility: string;
  tableColumnWidths: string;
  tableColumnOrder: string;
  tableSort: string;
  density: string;
  // Optional — only pages with a cards view provide these
  cardFieldsVisibility?: string;
  cardsSort?: string;
};

type TablePreferenceDefaults = {
  tableColumnVisibility: GridColumnVisibilityModel;
  tableSort: GridSortModel;
  cardFieldsVisibility?: GridColumnVisibilityModel;
  cardsSort?: GridSortModel;
};

/**
 * Composes all DataGrid persistence hooks (column visibility, widths, order,
 * sort, density) for a table page into a single hook call.
 *
 * Pages without a cards view omit the optional card keys from storageKeys and
 * defaults; the returned card values will be undefined in that case.
 */
export const useTablePreferences = (
  storageKeys: TablePreferenceStorageKeys,
  defaults: TablePreferenceDefaults,
) => {
  const {
    columnVisibilityModel: tableColumnVisibility,
    handleColumnToggle: handleTableColumnToggle,
    setColumnVisibilityModel: setTableColumnVisibility,
  } = useColumnVisibility(
    storageKeys.tableColumnVisibility,
    defaults.tableColumnVisibility,
  );

  const { sortModel: tableSortModel, setSortModel: setTableSortModel } =
    useSortModel(storageKeys.tableSort, defaults.tableSort);

  const { columnWidths, handleColumnWidthChange } = useColumnWidths(
    storageKeys.tableColumnWidths,
  );

  const { columnOrder, handleColumnOrderChange } = useColumnOrder(
    storageKeys.tableColumnOrder,
  );

  const { density, setDensity } = useDensity(storageKeys.density);

  const {
    columnVisibilityModel: cardFieldsVisibility,
    handleColumnToggle: handleCardFieldsToggle,
  } = useColumnVisibility(
    storageKeys.cardFieldsVisibility,
    defaults.cardFieldsVisibility ?? {},
  );

  const { sortModel: cardsSortModel, setSortModel: setCardsSortModel } =
    useSortModel(storageKeys.cardsSort, defaults.cardsSort ?? []);

  return {
    // Table preferences
    tableColumnVisibility,
    handleTableColumnToggle,
    setTableColumnVisibility,
    tableSortModel,
    setTableSortModel,
    columnWidths,
    handleColumnWidthChange,
    columnOrder,
    handleColumnOrderChange,
    density,
    setDensity,
    // Card preferences (undefined-equivalent when keys not provided)
    cardFieldsVisibility: storageKeys.cardFieldsVisibility
      ? cardFieldsVisibility
      : undefined,
    handleCardFieldsToggle: storageKeys.cardFieldsVisibility
      ? handleCardFieldsToggle
      : undefined,
    cardsSortModel: storageKeys.cardsSort ? cardsSortModel : undefined,
    setCardsSortModel: storageKeys.cardsSort ? setCardsSortModel : undefined,
  };
};
